/**
 * STAGE 5 幽影·暗界 —— 自机狙奇袭 + 暗色低可见弹 + 加速弹。
 * 小兵射型：暗弹单狙（ghost）/ 加速狙（imp）/ 暗环（wraith）/ 追踪 + 连狙（flayed）。
 */
import * as P from '../../game/patterns.js';
import { aimOf, fireTimer, accelBullet, shadowBullet, composeBullets, guideBullet, s5Mid, s5Big } from './bosses.js';

const DROP_STD = [{ type: 'P', weight: 2 }, { type: 'POINT', weight: 3 }]; // P 占比 40%
const DROP_POINT = [{ type: 'POINT', weight: 1 }];
const DROP_ELITE = [{ type: 'B', weight: 1 }, { type: 'STAR', weight: 1 }, { type: 'P', weight: 2 }]; // 精英必掉半数为 P

/** 幽魂：直线 + 暗色小弹单狙 */
function ghost(x, y, vx, vy) {
  return {
    x, y, hp: 16, r: 12, spriteKey: 's5_ghost', scoreValue: 300, drops: DROP_STD,
    script(e, dt, ctx) {
      e.x += vx * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 1.6)) {
        // 暗影单狙：远时只有 0.15 鬼影，逼近自机 190px 才显形（暗界低可见）
        P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 1, speed: 2.6, r: 3, color: 'orb_purple', jitter: 0.08, onUpdate: shadowBullet(190) });
      }
    },
  };
}

/** 小鬼：快速斜插 + 加速狙 */
function imp(x, y, vx, vy) {
  return {
    x, y, hp: 14, r: 11, spriteKey: 's5_imp', scoreValue: 350, drops: DROP_POINT,
    script(e, dt, ctx) {
      e.x += vx * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 360 && fireTimer(e, dt, 1.8)) {
        ctx.field.spawn({
          x: e.x, y: e.y, speed: 1.6, angle: aimOf(ctx, e.x, e.y), r: 3,
          color: 'pill_pink', onUpdate: accelBullet(1.0),
        });
      }
    },
  };
}

/** 亡灵：纵队慢压 + 暗色环 */
function wraith(x, y, vy) {
  return {
    x, y, hp: 24, r: 14, spriteKey: 's5_wraith', scoreValue: 500, drops: DROP_STD,
    script(e, dt, ctx) {
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 2.2)) {
        P.ring(ctx.field, e.x, e.y, e.age, 0, { n: 8, speed: 1.8, r: 3, offset: e.age * 0.4, color: 'orb_blue' });
      }
    },
  };
}

/** 剥皮魔精英：悬停追踪 + 5 连狙 */
function flayed(x) {
  return {
    x, y: -28, hp: 34, r: 16, spriteKey: 's5_flayed', scoreValue: 800, drops: DROP_ELITE,
    script(e, dt, ctx) {
      if (e.y < 140) {
        e.y += 1.7 * dt * 60;
      } else if ((e._hold = (e._hold || 0) + dt) < 4) {
        e.x += Math.sin(e.age * 1.8) * 0.8 * dt * 60;
        if (fireTimer(e, dt, 2.4)) {
          P.homing(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 2, speed: 1.7, homing: 0.55, r: 4, color: 'drop_pink', onUpdate: composeBullets(guideBullet(5), accelBullet(0.5)) });
        }
        if (fireTimer(e, dt, 1.2, '_ft2')) {
          P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 5, speed: 3.2, spread: 0.1, r: 3, color: 'orb_purple', jitter: 0.08 });
        }
      } else {
        e.y += 2.6 * dt * 60;
      }
    },
  };
}

export default {
  id: 5, name: '幽影·暗界', bg: 'bg5', bgm: 'stage5',
  *script(api) {
    const W = api.VIEW_W;
    yield { say: 'STAGE 5  幽影·暗界' };
    yield { wait: 1.0 };

    // W1 幽魂纵队
    yield { wave: [0, 1, 2, 3].map((i) => ghost(W / 2, -24 - i * 44, 0, 1.7)) };
    yield { wait: 3.5 };
    // W2 小鬼交叉奇袭
    yield {
      wave: [
        ...[0, 1, 2].map((i) => imp(50 + i * 40, -24 - i * 34, 1.3, 2.0)),
        ...[0, 1, 2].map((i) => imp(W - 50 - i * 40, -24 - i * 34, -1.3, 2.0)),
      ],
    };
    yield { wait: 4.0 };
    // W3 亡灵双纵队（暗环封场）
    yield {
      wave: [
        ...[0, 1].map((i) => wraith(130, -24 - i * 90, 1.1)),
        ...[0, 1].map((i) => wraith(W - 130, -60 - i * 90, 1.1)),
      ],
    };
    yield { wait: 4.5 };
    // W4 精英剥皮魔
    yield { wave: [flayed(W / 2)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s5Mid() };
    yield { wait: 2.0 };

    // W5 包夹（道中二，强度上升）
    yield {
      wave: [
        ...[0, 1, 2].map((i) => ghost(-20, 80 + i * 44, 1.6, 0.5)),
        ...[0, 1, 2].map((i) => ghost(W + 20, 100 + i * 44, -1.6, 0.5)),
      ],
    };
    yield { wait: 4.0 };
    // W6 小鬼 + 亡灵混编
    yield {
      wave: [
        ...[0, 1].map((i) => wraith(140 + i * 200, -24, 1.2)),
        ...[0, 1, 2].map((i) => imp(80 + i * 160, -60, i % 2 === 0 ? 0.8 : -0.8, 2.2)),
      ],
    };
    yield { wait: 4.0 };
    // W7 双剥皮魔
    yield { wave: [flayed(140), flayed(W - 140)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s5Big() };
    yield { say: 'STAGE 5 CLEAR' };
  },
};
